import { useEffect, useState, FC } from 'react';
import appState from '../../../state/AppStateContainer';
import { BidFormContentContainer } from '../StaticComponents';
import CollapsibleView from '../CollapsibleView/CollapsibleView';
import { SupplierAccountCard, SupplierStatus } from '../SupplierAccountCard/supplierAccountCard';
import { useTranslation } from 'react-i18next';
import ServiceGoogleMaps from '../../../services/service-google-maps';
import ShowMoreText from '../../../components/shared/ShowMoreText';
import HTMLParser from '../../../components/shared/HTMLParser/HTMLParser';
import { getImageUrl } from '../../../components/shared/ImagePreview/ImagePreview';
import { FileScope } from '../../../util/appEnum';
import FilePreviewOverlay from '../../../components/shared/FilePreviewOverlay/FilePreviewOverlay';
import { isPreviewable } from '../../../components/shared/FilePreviewViewer/FilePreviewViewer';
import { ServiceDownload } from '../../../services/service-download';
import { isHtmlEmpty } from '../../../util';
// Imported here rather than relying on page-quotation.jsx having loaded it:
// this component also renders on the public bid request and internal bid form.
import '../page-quotation.css';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'btn-remove-industry': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      'bt-check': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      'bt-row': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      'bt-button': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      'bt-column': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

const sortVersions = (a: VersionData, b: VersionData) => {
  if (parseInt(a.quoteVersionNo) === parseInt(b.quoteVersionNo)) return 0;
  return parseInt(a.quoteVersionNo) > parseInt(b.quoteVersionNo) ? -1 : 1;
}

export type VersionData = {
  quotation: {
    id: number,
    project: any,
    demands: {demandId: {_id: string}}[],
    area: any,
    attachments: any[],
    priceDetails: {
      priceType: {
        name: string,
        _id: string
      },
      price: number
    }[],
    content: string
  },
  demands: {
    demandId: string
  }[],
  supplierBid?: {
    demands: {demandId: string}[],
    attachments: any[],
    message: string | null,
    content: string | null,
    price: number | null,
    priceDetails: {
      priceType: {
        name: string,
        _id: string
      },
      price: number
    }[],
    quotationValidity: string
  },
  supplier: {
    slug: string,
    _id: string,
    name: string
  },
  supplierSlug: string,
  supplierMemberId: string,
  supplierMembers?: { _id: string, name: string, email: string }[],
  quoteVersionNo: string,
  quoteRefId: string,
  status: {
    expirationDate: string,
    isLocked: boolean,
    reason: string,
    submittedAt: string
  },
  intentStatus: {
    intent: string,
    declaredAt: string,
    declaredBy: string,
    reasonDeclined: string
  },
}

type Props = {
  quotationVersions: VersionData[],
  orgId?: string,
  onSubmit?: () => void,
  isPublished?: boolean,
  supplierStatus?: SupplierStatus,
};

const QuotationForm: FC<Props> = ({ quotationVersions, onSubmit, isPublished, supplierStatus, orgId }) => {
  const [t] = useTranslation('quotation', { keyPrefix: 'form' });
  const [projectData, setProjectData] = useState<{
    name: string,
    number: string,
    orgName: string,
    phase?: string,
    address?: { formatted?: string, lat?: number, lng?: number },
    id?: string,
    projectInformation?: string,
    image?: { file?: string, fileName?: string }
  }>();
  const [mapsBrowserKey, setMapsBrowserKey] = useState<string>('');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [supplierData, setSupplierData] = useState<{
    name: string
  }>();
  const [versions, setVersions] = useState<VersionData[]>(quotationVersions.sort(sortVersions));

  useEffect(() => {
    setVersions(quotationVersions.sort(sortVersions));
  }, [quotationVersions]);

  useEffect(() => {
    if (versions?.[0]) {
      const { quotation, supplier } = versions[0];
      setProjectData(quotation.project);
      setSupplierData(supplier);
    }
  }, [versions.length]);

  const address = projectData?.address;
  const formattedAddress = address?.formatted;

  useEffect(() => {
    if (!formattedAddress) return;
    let cancelled = false;
    ServiceGoogleMaps.getBrowserKey().then(key => {
      if (!cancelled) setMapsBrowserKey(key);
    });
    return () => { cancelled = true; };
  }, [formattedAddress]);

  const renderAddress = () => {
    if (!formattedAddress) return null;

    // Prefer the resolved coordinates so the pin lands on the exact site; fall
    // back to letting Google geocode the text.
    const mapQuery = (address?.lat != null && address?.lng != null)
      ? `${address.lat},${address.lng}`
      : formattedAddress;

    return (
    <div className="info-address-container">
      {renderInfo(t('title.address', 'Adress'), formattedAddress)}
      {mapsBrowserKey ? (
        <iframe
          className="info-address-map"
          title={t('title.address', 'Adress')}
          src={`https://www.google.com/maps/embed/v1/place?key=${mapsBrowserKey}&q=${encodeURIComponent(mapQuery)}&language=sv`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      ) : (
        // No embed key configured - never render a broken frame, just link out.
        <a
          className="info-address-link"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('button.showOnMap', 'Visa på karta')}
        </a>
      )}
    </div>
    );
  };

  const renderProjectInfoAndImage = () => {
    // Rich text, so emptiness cannot be judged by trimming the markup:
    // `<p>&nbsp;</p>` is truthy but renders as nothing, and would otherwise
    // draw a labelled empty column plus the centre divider.
    const raw = projectData?.projectInformation;
    const information = isHtmlEmpty(raw) ? '' : raw;
    const imageFile = projectData?.image?.file;
    const imageName = projectData?.image?.fileName;
    const imageUrl = imageFile && projectData?.id
      ? getImageUrl(imageFile, appState.getCurrentOrgSlug(), FileScope.ProjectImage, { projectId: projectData.id })
      : '';

    // The inline <img> uses the /util/image route, which 302s to an Azure SAS
    // URL - cheap, but the storage account has no CORS rules, so the shared
    // preview overlay (which XHRs the URL as a blob) needs the same-origin
    // /util/download route instead.
    const previewUrl = imageFile && projectData?.id && imageName && isPreviewable(imageName)
      ? ServiceDownload.getFileDownloadURL(imageFile, FileScope.ProjectImage, { projectId: projectData.id })
      : '';

    if (!information && !imageUrl) return null;

    // One column when only one side has content, so there is no empty cell and
    // no divider hanging off nothing.
    const single = !information || !imageUrl;

    return (
      <div className={`info-extra-container${single ? ' info-extra-container--single' : ''}`}>
        {information && (
          <div className="info-extra-text">
            <div className="title">{t('title.projectInformation', 'Projektinformation')}</div>
            {/* Stored as TinyMCE HTML; HTMLParser is the only sanctioned render
                path (DOMPurify). ShowMoreText clamps it so long content cannot
                push the image column or the map around. */}
            <ShowMoreText customClass="info-extra-showmore">
              <HTMLParser data={information} />
            </ShowMoreText>
          </div>
        )}
        {imageUrl && (
          <div className="info-extra-image">
            <div className="title">{t('title.projectImage', 'Projektbild')}</div>
            <div
              className={`info-extra-image-box${previewUrl ? ' info-extra-image-box--clickable' : ''}`}
              onClick={previewUrl ? () => setShowImagePreview(true) : undefined}
              role={previewUrl ? 'button' : undefined}
              tabIndex={previewUrl ? 0 : undefined}
              onKeyDown={previewUrl ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowImagePreview(true); }
              } : undefined}
            >
              <img src={imageUrl} alt={t('title.projectImage', 'Projektbild')} />
            </div>
            {showImagePreview && previewUrl && (
              <FilePreviewOverlay
                url={previewUrl}
                fileName={imageName as string}
                onClose={() => setShowImagePreview(false)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderHeaderInfo = () => projectData && (
    <div style={{ margin: '26px 0', marginTop: versions[0].supplierMemberId ? '26px' : '0' }} className="generic-card">
      <div className="info-head-container">
        {renderInfo(t('title.orgName', 'Beställare'), projectData.orgName ?? appState.getCurrentOrgName())}
        {renderInfo(t('title.project', 'Projekt'), projectData.name)}
        {projectData.number && renderInfo(t('title.projectNo', 'Projektnummer'), projectData.number)}
        {renderInfo(t('title.phase', 'Skede'), projectData.phase ?? '-')}
        {versions?.[0]?.quotation?.area?.name && renderInfo(t('title.area', 'Yrkeskategori'), versions?.[0]?.quotation?.area?.name)}
      </div>
      {renderProjectInfoAndImage()}
      {renderAddress()}
    </div>
  );

  const renderInfo = (title: string, info: string) => (
    <div>
      <div className="title">{title}</div>
      <div className="content">{info}</div>
    </div>
  );

  return (projectData && (isPublished || supplierData) && versions?.length > 0) ? (
    <BidFormContentContainer projectData={projectData} supplierData={supplierData} isSupplierView={versions[0].supplierMemberId}>
      <SupplierAccountCard supplierStatus={supplierStatus} />

      {renderHeaderInfo()}

      {versions?.map((versionData, i) => (
        <CollapsibleView
          openView={i == 0}
          key={versionData?.quotation?.id}
          versionData={versionData}
          onSubmit={onSubmit}
          isPublished={isPublished}
          orgId={orgId}
        />
      ))}
    </BidFormContentContainer>
  ) : null;
}

export default QuotationForm;
